import {useViewOrdersContext} from "v4/pages/admin/ViewOrders/contexts/ViewOrdersContext";
import {useEffect, useMemo, useRef, useState} from "react";
import {CHOICE_TYPE} from "v4/data/fieldTypes";

export default function useViewOrdersConfigButtonConditionalField(group, field, config) {
    const {
        currentViewOrder: {state, formFields},
        setFieldConfig
    } = useViewOrdersContext();

    /** @type {{current: ?HTMLUListElement}} */
    const submenuRef = useRef();
    const [listFieldKeyChosen, setListFieldKeyChosen] = useState(null);
    const [hasEnoughSpace, setHasEnoughSpace] = useState(true);

    const listFields = useMemo(() => {
        const allowedListFields = state.map(({isDefault, fields}) => {
            if (isDefault) return [];

            return fields.filter(_field => {
                // Must be choice type
                if (_field.type !== CHOICE_TYPE) return false;

                // Not self-referencing
                if (_field.key === field.key) return false;

                // Not already conditioning this field
                return ![`sf_${field.key}`, field.key].includes(_field.conditionField);
            });
        }).flat().filter(Boolean).map(({key}) => key);

        function getNestedField(listField, compoundKey) {
            if (!listField.properties) return null;

            const parts = compoundKey.split('_');
            let currentField = listField;
            for (let i = 1; i < parts.length; i++) {
                const part = parts[i];

                if (!currentField.properties || !currentField.properties[part]) {
                    return null;
                }

                currentField = currentField.properties[part];
            }

            return currentField;
        }

        return Object.entries(formFields).reduce((acc, [listFieldKey, listField]) => {
            if (!listField.properties && (listField.items?.enum_titles ?? listField.enum_titles ?? []).length === 0) return acc;

            if (allowedListFields.includes(listFieldKey)) {
                acc.push([listFieldKey, listField]);
                return acc;
            }

            // Check for compound keys like "company_structureType"
            for (const allowedKey of allowedListFields) {
                if (allowedKey.startsWith(`${listFieldKey}_`)) {
                    const nestedField = getNestedField(listField, allowedKey);

                    if (!nestedField || (!nestedField.properties && (nestedField.items?.enum_titles ?? nestedField.enum_titles ?? []).length === 0)) return acc;

                    acc.push([allowedKey, nestedField]);
                }
            }

            return acc;
        }, []);
    }, [field.key, formFields, state]);

    const onListFieldKeyChoose = (listFieldKey) => (e) => {
        e.stopPropagation();
        setListFieldKeyChosen(listFieldKey);
    }

    const onSelect = (listFieldKey, listField, enumIndex) => (e) => {
        e.stopPropagation();

        const enums = listField.items?.enum ?? listField.enum ?? [];
        const selectedValue = enums[enumIndex];
        const values = (field.conditionValue ?? '').split(',').filter(Boolean);
        const isSelected = values.includes(selectedValue);

        let listFieldKeyPrefix = '';

        if (listField.attr?.['data-type'] === 'specific-fields') {
            listFieldKeyPrefix = 'sf_';
        }

        let newValues;
        if (isSelected) {
            newValues = values.filter(v => v !== selectedValue);
        } else if (field.conditionField === `${listFieldKeyPrefix}${listFieldKey}`) {
            newValues = [...values, selectedValue];
        } else {
            newValues = [selectedValue];
        }

        setFieldConfig(group.id, field.id, {
            ...config,
            conditionField: newValues.length ? `${listFieldKeyPrefix}${listFieldKey}` : null,
            conditionValue: newValues.length ? newValues.join(',') : null
        });
    }


    useEffect(() => {
        if (!submenuRef.current) return true;

        const parentReference = submenuRef.current.closest('.new-view-orders__scrollable');
        if (!parentReference) return true;

        const submenuRect = submenuRef.current.getBoundingClientRect();
        const parentRect = parentReference.getBoundingClientRect();

        setHasEnoughSpace(submenuRect.right <= parentRect.right && submenuRect.left >= parentRect.left);
    }, []);

    return {
        submenuRef,
        listFields,
        listFieldKeyChosen,
        hasEnoughSpace,
        onListFieldKeyChoose,
        onSelect,
    }
}
